import type {DateFormat, DateSegment} from '@helpers';

import {diffText} from './textDiff';

interface SegmentRule {
  capacity: number;
  zeroPad: boolean;
}

const SEGMENT_RULES: Record<DateSegment, SegmentRule> = {
  day: {capacity: 2, zeroPad: true},
  month: {capacity: 2, zeroPad: true},
  year: {capacity: 4, zeroPad: false},
};

export type Parts = Record<DateSegment, string>;

/** Logical caret: a position within a segment, independent of separators and display reflows. */
export interface Caret {
  segment: DateSegment;
  local: number;
}

export interface SelectionRange {
  start: number;
  end: number;
}

/** Value region of a segment, in display coordinates. */
interface Span {
  segment: DateSegment;
  start: number;
  end: number;
}

export interface Layout {
  display: string;
  spans: Span[];
  order: DateSegment[];
}

export interface EditResult {
  parts: Parts;
  caret: Caret;
}

const EMPTY_PARTS: Parts = {day: '', month: '', year: ''};

const onlyDigits = (raw: string): string => raw.replace(/[^0-9]/g, '');

const isFull = (parts: Parts, segment: DateSegment): boolean =>
  parts[segment].length >= SEGMENT_RULES[segment].capacity;

const clip = (raw: string, segment: DateSegment): string => onlyDigits(raw).slice(0, SEGMENT_RULES[segment].capacity);

export const toISO = (parts: Parts): string => {
  if (!parts.day && !parts.month && !parts.year) return '';
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const fromISO = (value: string | undefined): Parts => {
  if (!value) return {...EMPTY_PARTS};
  const [year = '', month = '', day = ''] = value.split('-');
  return {day: clip(day, 'day'), month: clip(month, 'month'), year: clip(year, 'year')};
};

export const buildLayout = (parts: Parts, {order, separator}: DateFormat): Layout => {
  let lastContent = -1;
  order.forEach((segment, index) => {
    if (parts[segment]) lastContent = index;
  });
  if (lastContent === -1) return {display: '', spans: [], order};

  const invitesNext = isFull(parts, order[lastContent]) && lastContent < order.length - 1;
  const lastVisible = invitesNext ? lastContent + 1 : lastContent;

  let display = '';
  const spans: Span[] = [];
  for (let index = 0; index <= lastVisible; index += 1) {
    const segment = order[index];
    if (index > 0) display += separator;
    const start = display.length;
    display += parts[segment];
    spans.push({segment, start, end: display.length});
  }
  return {display, spans, order};
};

export const caretToOffset = ({segment, local}: Caret, layout: Layout): number => {
  const span = layout.spans.find(candidate => candidate.segment === segment);
  if (!span) return layout.display.length;
  return span.start + Math.max(0, Math.min(local, span.end - span.start));
};

// A caret inside a separator snaps to the start of the following segment.
export const offsetToCaret = (offset: number, layout: Layout): Caret => {
  const clamped = Math.max(0, Math.min(offset, layout.display.length));
  for (const span of layout.spans) {
    if (clamped <= span.end) return {segment: span.segment, local: Math.max(0, clamped - span.start)};
  }
  return {segment: layout.order[0], local: 0};
};

/** Returns the same reference when nothing changes, so callers can detect no-ops by identity. */
export const padSegment = (parts: Parts, segment: DateSegment): Parts => {
  const value = parts[segment];
  const applies = SEGMENT_RULES[segment].zeroPad && value.length === 1 && value !== '0';
  return applies ? {...parts, [segment]: `0${value}`} : parts;
};

export const padSegments = (parts: Parts): Parts =>
  (Object.keys(SEGMENT_RULES) as DateSegment[]).reduce(padSegment, parts);

const overlapsValue = (layout: Layout, start: number, end: number): boolean =>
  layout.spans.some(span => Math.max(start, span.start) < Math.min(end, span.end));

const segmentBefore = (layout: Layout, offset: number): DateSegment | null => {
  for (let index = layout.spans.length - 1; index >= 0; index -= 1) {
    const span = layout.spans[index];
    if (span.end <= offset && span.end > span.start) return span.segment;
  }
  return null;
};

const advanceWhenFull = (caret: Caret, parts: Parts, order: DateSegment[]): Caret => {
  const index = order.indexOf(caret.segment);
  const hasNext = index >= 0 && index + 1 < order.length;
  if (!hasNext || caret.local < SEGMENT_RULES[caret.segment].capacity) return caret;
  return {segment: order[index + 1], local: 0};
};

const insertDigits = (parts: Parts, order: DateSegment[], anchor: Caret, digits: string): EditResult => {
  const next: Parts = {...parts};
  let segmentIndex = Math.max(0, order.indexOf(anchor.segment));
  let local = Math.min(anchor.local, next[anchor.segment].length);
  let landed = false;

  for (const digit of digits) {
    let targetIndex = segmentIndex;
    let targetLocal = local;
    while (targetIndex < order.length && isFull(next, order[targetIndex])) {
      targetIndex += 1;
      targetLocal = 0;
    }
    if (targetIndex >= order.length) break;

    const segment = order[targetIndex];
    const at = Math.min(targetLocal, next[segment].length);
    next[segment] = `${next[segment].slice(0, at)}${digit}${next[segment].slice(at)}`;
    segmentIndex = targetIndex;
    local = at + 1;
    landed = true;
  }

  const caret: Caret = {segment: order[segmentIndex], local};
  return {parts: next, caret: landed ? advanceWhenFull(caret, next, order) : caret};
};

export const applyRawChange = (parts: Parts, format: DateFormat, raw: string): EditResult => {
  const layout = buildLayout(parts, format);
  const edit = diffText(layout.display, raw);
  const inserted = onlyDigits(edit.inserted);

  const deletesOnlySeparator = inserted === '' && edit.end > edit.start && !overlapsValue(layout, edit.start, edit.end);
  if (deletesOnlySeparator) {
    const segment = segmentBefore(layout, edit.start);
    if (!segment) return {parts, caret: offsetToCaret(edit.start, layout)};
    const value = parts[segment];
    return {parts: {...parts, [segment]: value.slice(0, -1)}, caret: {segment, local: value.length - 1}};
  }

  const trimmed: Parts = {...parts};
  layout.spans.forEach(span => {
    const from = Math.max(edit.start, span.start) - span.start;
    const to = Math.min(edit.end, span.end) - span.start;
    if (to > from) trimmed[span.segment] = `${trimmed[span.segment].slice(0, from)}${trimmed[span.segment].slice(to)}`;
  });

  return insertDigits(trimmed, format.order, offsetToCaret(edit.start, layout), inserted);
};
